import { useRef, useEffect, useState } from 'react';

import { initiateOauthFlowApi, completeOauthFlowApi, OauthFlowPayload } from '@api';
import useStores from '@stores/useStores';

import { OAUTH_POST_MESSAGE_TYPE, OAUTH_CHANNEL_NAME } from '../../../../constants';

const INITIATE_OAUTH_FAILURE_MESSAGE = 'Unable to initiate oauth flow. Please try again';
const VENDOR_AUTH_REDIRECT_URL_FAILURE_MESSAGE =
  'Destination auth redirect url failed. Please try again';
const OAUTH_COMPLETION_FLOW_FAILURE_MESSAGE = 'Oauth flow could not be completed. Please try again';

interface UseOAuthProps {
  onComplete: (accountId: string) => void;
  onError?: (errorMessage: string) => void;
}

export const useOAuth = ({ onComplete, onError }: UseOAuthProps) => {
  const { messagesStore } = useStores();
  const [loading, setLoading] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const oAuthPayloadRef = useRef<OauthFlowPayload | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const popupMonitorRef = useRef<number | null>(null);
  const messageReceivedRef = useRef(false);

  // Common error handler
  const handleError = (message: string) => {
    setLoading?.(false);
    messagesStore?.showErrorMessage(message);
    onError?.(message);
  };

  const openPopup = () => {
    popupRef.current = window.open('', '_blank') as Window;
    popupRef.current.document.write('Loading…');
    startPopupMonitor();
  };

  const closePopup = () => {
    if (popupRef.current) {
      popupRef.current.close();
      popupRef.current = null;
    }
    clearPopupMonitor();
  };

  const startPopupMonitor = () => {
    clearPopupMonitor();
    popupMonitorRef.current = window.setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        if (!messageReceivedRef.current) {
          handleError('OAuth popup was closed before completion.');
        }
        clearPopupMonitor();
      }
    }, 500);
  };

  const clearPopupMonitor = () => {
    if (popupMonitorRef.current) {
      clearInterval(popupMonitorRef.current);
      popupMonitorRef.current = null;
    }
  };

  const initiateOAuthFlow = async () => {
    try {
      const { data } = await initiateOauthFlowApi(oAuthPayloadRef.current!);

      if (popupRef.current) {
        popupRef.current.location.href = data.url;
      }
    } catch (err: unknown) {
      closePopup();
      handleError(err instanceof Error ? err.message : INITIATE_OAUTH_FAILURE_MESSAGE);
    }
  };

  const completeOAuthFlow = async (search: string) => {
    try {
      const { data } = await completeOauthFlowApi(oAuthPayloadRef.current!, search);
      onComplete(data.accountId);
    } catch (err: unknown) {
      handleError(typeof err === 'string' ? err : OAUTH_COMPLETION_FLOW_FAILURE_MESSAGE);
    } finally {
      setLoading?.(false);
    }
  };

  const handleChannelMessage = (event: MessageEvent) => {
    const { data } = event;
    if (!data || data.type !== OAUTH_POST_MESSAGE_TYPE) {
      return;
    }
    // Only handle once
    channelRef.current?.removeEventListener('message', handleChannelMessage);
    messageReceivedRef.current = true;
    const { code, error, search } = data.payload;

    if (error || !code) {
      handleError(error || VENDOR_AUTH_REDIRECT_URL_FAILURE_MESSAGE);
      closePopup();
      return;
    }

    completeOAuthFlow(search);
    closePopup();
  };

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (channelRef.current) {
        channelRef.current.close();
        channelRef.current = null;
      }
      closePopup();
      clearPopupMonitor();
      messageReceivedRef.current = false;
    },
    [],
  );

  const startOAuth = async (oAuthPayload: OauthFlowPayload) => {
    try {
      // Setup BroadcastChannel
      if (channelRef.current) {
        channelRef.current.close();
      }

      channelRef.current = new window.BroadcastChannel(OAUTH_CHANNEL_NAME);
      channelRef.current.addEventListener('message', handleChannelMessage);
      oAuthPayloadRef.current = oAuthPayload;
      setLoading?.(true);
      openPopup();
      initiateOAuthFlow();
    } catch (error) {}
  };

  return {
    loading,
    startOAuth,
    startReOAuth: ({ accountId }: { accountId: string }) => startOAuth({ accountId }),
  };
};
